import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Save, Clock, Calendar, Timer, MousePointerClick, MessageCircleOff, RotateCcw, Snowflake } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface WaitClickConfigProps {
    node: FlowNode;
    onClose: () => void;
}

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days';
type Behavior = 'ignore' | 'restart' | 'freeze';

const SECONDS_IN: Record<TimeUnit, number> = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
};

const UNIT_LIMITS: Record<TimeUnit, { min: number; max: number; label: string; Icon: typeof Clock }> = {
    seconds: { min: 1, max: 59, label: 'Segundos', Icon: Timer },
    minutes: { min: 1, max: 59, label: 'Minutos', Icon: Clock },
    hours: { min: 1, max: 23, label: 'Horas', Icon: Clock },
    days: { min: 1, max: 365, label: 'Dias', Icon: Calendar },
};

const inferUnit = (seconds: number): TimeUnit => {
    if (seconds >= 86400 && seconds % 86400 === 0) return 'days';
    if (seconds >= 3600 && seconds % 3600 === 0) return 'hours';
    if (seconds >= 60 && seconds % 60 === 0) return 'minutes';
    return 'seconds';
};

export default function WaitClickConfig({ node, onClose }: WaitClickConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const initialSeconds = node.data.config?.delay_seconds || 3600;
    const initialUnit = inferUnit(initialSeconds);

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [timeValue, setTimeValue] = useState(initialSeconds / SECONDS_IN[initialUnit]);
    const [timeUnit, setTimeUnit] = useState<TimeUnit>(initialUnit);
    const [behavior, setBehavior] = useState<Behavior>(
        (node.data.wait_on_message_behavior as Behavior) || 'ignore'
    );

    const totalSeconds = Math.max(0, Math.floor(timeValue * SECONDS_IN[timeUnit]));
    const unitInfo = UNIT_LIMITS[timeUnit];
    const UnitIcon = unitInfo.Icon;

    const saveMutation = useMutation({
        mutationFn: () => updateNode(node.data.id, {
            node_name: nodeName,
            wait_on_message_behavior: behavior,
            config: { delay_seconds: totalSeconds },
        }),
        onSuccess: () => {
            toast({ title: 'Wait For Click salvo!', description: 'Configurações atualizadas.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    return (
        <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Nome */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Nome do Node</Label>
                    <Input
                        value={nodeName}
                        onChange={(e) => setNodeName(e.target.value)}
                        placeholder="Ex: Aguarda click na oferta"
                        className="h-10"
                    />
                </div>

                {/* Tempo */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        Tempo máximo de espera
                    </Label>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-xs text-gray-500">Valor</Label>
                            <Input
                                type="number"
                                min={unitInfo.min}
                                max={unitInfo.max}
                                value={timeValue}
                                onChange={(e) => setTimeValue(Number(e.target.value))}
                                className="h-10 text-lg font-semibold"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-500">Unidade</Label>
                            <Select
                                value={timeUnit}
                                onValueChange={(value: TimeUnit) => {
                                    setTimeUnit(value);
                                    const lim = UNIT_LIMITS[value];
                                    if (timeValue < lim.min) setTimeValue(lim.min);
                                    if (timeValue > lim.max) setTimeValue(lim.max);
                                }}
                            >
                                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(Object.keys(UNIT_LIMITS) as TimeUnit[]).map((u) => {
                                        const Icon = UNIT_LIMITS[u].Icon;
                                        return (
                                            <SelectItem key={u} value={u}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4" />
                                                    <span>{UNIT_LIMITS[u].label}</span>
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-3">
                        <UnitIcon className="w-5 h-5 text-orange-600" />
                        <div className="text-sm">
                            <span className="font-bold text-orange-900">{timeValue}</span>
                            <span className="text-orange-800"> {unitInfo.label.toLowerCase()} </span>
                            <span className="text-orange-700 text-xs">({totalSeconds.toLocaleString()}s)</span>
                        </div>
                    </div>
                </div>

                {/* Comportamento ao receber mensagem texto */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <MousePointerClick className="w-3.5 h-3.5" />
                        Se o lead mandar mensagem durante a espera
                    </Label>

                    <div className="space-y-2">
                        <BehaviorOption
                            value="ignore"
                            current={behavior}
                            onSelect={setBehavior}
                            Icon={MessageCircleOff}
                            label="Ignora a mensagem"
                            desc="Wait click continua ativo. Mensagem do lead não dispara nada."
                        />
                        <BehaviorOption
                            value="restart"
                            current={behavior}
                            onSelect={setBehavior}
                            Icon={RotateCcw}
                            label="Cancela e reinicia o flow"
                            desc="Mata o wait click pendente e roda o trigger normal de novo."
                        />
                        <BehaviorOption
                            value="freeze"
                            current={behavior}
                            onSelect={setBehavior}
                            Icon={Snowflake}
                            label="Congela tudo"
                            desc="Nada acontece até clicar num botão ou expirar."
                        />
                    </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-900">
                        💡 <strong>Dica:</strong> Conecte este node a um Template/Foto/Vídeo que tenha botões URL. Os ramos de cada botão aparecem automaticamente.
                    </p>
                </div>
            </div>

            <div className="border-t bg-white p-6 flex-shrink-0">
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || totalSeconds < 1}
                    className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-medium"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
            </div>
        </>
    );
}

// ────────────────────────────────────────────────────────────────────────

function BehaviorOption({
    value,
    current,
    onSelect,
    Icon,
    label,
    desc,
}: {
    value: Behavior;
    current: Behavior;
    onSelect: (v: Behavior) => void;
    Icon: typeof MessageCircleOff;
    label: string;
    desc: string;
}) {
    const active = current === value;
    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-start gap-3 ${
                active
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
        >
            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? 'text-orange-600' : 'text-gray-400'}`} />
            <div className="flex-1">
                <div className={`text-sm font-semibold ${active ? 'text-orange-900' : 'text-gray-800'}`}>{label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
            </div>
        </button>
    );
}
